import SubscriptionSettings from '../SubscriptionSettings';
import { uiText, useUiLanguage } from '@/i18n/interface';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowUpRight } from 'lucide-react';
import BeltManagement from '../BeltManagement';
import AdminProfileManager from '../AdminProfileManager';
import RefactoredAdminSyllabusManager from '../syllabus/RefactoredAdminSyllabusManager';
import AdminPaymentManager from '../AdminPaymentManager';
import DojoSettings from '../DojoSettings';
import ColorThemeSettings from '../ColorThemeSettings';
import ParticipationStatsTab from './ParticipationStatsTab';
import OnDemandTrainingManager from './OnDemandTrainingManager';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { adminSections } from './adminSections';
import AdminNavigation from './AdminNavigation';
import StudentsListDialog from '../StudentsListDialog';

interface Props { activeTab?: string; onTabChange?: (tab: string) => void; overview?: ReactNode; paymentTab?: string; }
export default function AdminTabs({ activeTab: external, onTabChange, overview, paymentTab }: Props) {
  useUiLanguage();
  const [internal, setInternal] = useState('belts');
  const active = external || internal;
  const { t } = useTranslation('admin');
  const content: Record<string, () => ReactNode> = {
    overview: () => overview,
    students: () => <StudentsListDialog embedded />,
    belts: () => <BeltManagement />,
    syllabus: () => <RefactoredAdminSyllabusManager />,
    payments: () => <AdminPaymentManager initialTab={paymentTab} />,
    profiles: () => <AdminProfileManager />,
    participation: () => <ParticipationStatsTab />,
    training: () => <OnDemandTrainingManager />,
    settings: () => <div className="space-y-6"><SubscriptionSettings /><DojoSettings /><Card><CardHeader><CardTitle>{t("admin:management.advancedSystemSettings", { defaultValue: 'Appearance & preferences' })}</CardTitle><CardDescription>{t("admin:settings.advancedDescription", { defaultValue: 'Personalise your workspace.' })}</CardDescription></CardHeader><CardContent><ColorThemeSettings /></CardContent></Card></div>,
    schedule: () => <Card><CardHeader><CardTitle>{t("admin:management.trainingSchedule", { defaultValue: 'Training schedule' })}</CardTitle><CardDescription>{t("admin:descriptions.schedule", { defaultValue: 'Manage sessions, attendance and recurring training.' })}</CardDescription></CardHeader><CardContent><Button asChild><Link to="/schedule"><Calendar className="h-4 w-4" />{t("admin:management.trainingSchedule", { defaultValue: 'Training schedule' })}<ArrowUpRight className="h-4 w-4" /></Link></Button></CardContent></Card>,
  };
  const section = adminSections.find(s => s.value === active);
  return <div className="space-y-5">
    {!external && <AdminNavigation activeTab={active} onTabChange={onTabChange || setInternal} />}
    {active !== 'overview' && section && <div className="flex items-center gap-2"><section.icon className="h-5 w-5 text-primary" /><h2 className="wh-section-heading">{t(section.key, { defaultValue: section.fallback })}</h2></div>}
    <section id="admin-workspace-panel" aria-label={section ? t(section.key, { defaultValue: section.fallback }) : uiText("Admin workspace")}>{(content[active] || content.belts)()}</section>
  </div>;
}
